import { useState, useContext, useMemo, useEffect } from "react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { ThumbsUp, ThumbsDown, Sparkles, Settings } from "lucide-react";
import { cn } from "@/modern/utils";
import BryntumContext from "@/react/context/bryntumContext.jsx";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { assignAITags } from "@/network/network.jsx";
import { getSavedBoardSelection } from "@/react/hooks/useBoards";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import { GRANTS } from "@/other/constants/PrivilegeTypes.jsx";
import { toast } from "sonner";
import { useIntegrationErrorReporter } from "@/integrations";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { IncentiveTag } from "@/bryntum-lib/models/IncentiveTag.jsx";
import { IncentiveTagCategory } from "@/bryntum-lib/models/IncentiveTagCategory.jsx";
import { SETTING_KEYS } from "@/react/components/entityViews/settings/constants/settingsStructure.js";
import { SpotIncentivePickerShell, type SpotIncentivePickerPresentation } from "./SpotIncentivePickerShell";

interface PointsAssignmentProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pointsAssignment: {
    positiveTagIds: number[];
    negativeTagIds: number[];
  };
  onPointsChange: (points: { positiveTagIds: number[]; negativeTagIds: number[] }) => void;
  documentId?: number;
  editorInstance?: unknown;
  presentation?: SpotIncentivePickerPresentation;
}

const SpotIncentivePointsAssignment = ({
  open,
  onOpenChange,
  pointsAssignment,
  onPointsChange,
  documentId,
  presentation = "dialog",
}: PointsAssignmentProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { project, user, viewManager } = useContext(BryntumContext) as any;
  const { reportError } = useIntegrationErrorReporter();
  const [localPositiveTagIds, setLocalPositiveTagIds] = useState<number[]>(
    pointsAssignment.positiveTagIds || [],
  );
  const [localNegativeTagIds, setLocalNegativeTagIds] = useState<number[]>(
    pointsAssignment.negativeTagIds || [],
  );
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    setLocalPositiveTagIds(pointsAssignment.positiveTagIds || []);
    setLocalNegativeTagIds(pointsAssignment.negativeTagIds || []);
  }, [pointsAssignment.positiveTagIds, pointsAssignment.negativeTagIds]);

  const { version: incentiveTagsVersion } = useSwivuStore<IncentiveTag>({
    storeId: STORE_KEYS.incentiveTagsStore,
  });
  const { version: incentiveTagCategoryVersion } = useSwivuStore<IncentiveTagCategory>({
    storeId: STORE_KEYS.incentiveTagCategoryStore,
  });

  const tags = useMemo(() => {
    if (!project?.incentiveTagsStore) return [];
    const allTags = project.incentiveTagsStore.query(() => true, true) || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return allTags.map((tag: any) => ({
      id: Number(tag.id),
      name: tag.name,
      categoryId: tag.categoryId,
      categoryName: tag.categoryRecord.name,
      categoryColor: tag.categoryRecord.color,
      categoryPoints: tag.categoryRecord.points,
    }));
  }, [project, incentiveTagsVersion]);

  const categories = useMemo(() => {
    const categoryMap = new Map();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tags.forEach((tag: any) => {
      const categoryKey = tag.categoryId;
      if (!categoryMap.has(categoryKey)) {
        categoryMap.set(categoryKey, {
          id: categoryKey,
          name: tag.categoryName,
          color: tag.categoryColor,
          points: tag.categoryPoints,
          tags: [],
        });
      }
      categoryMap.get(categoryKey).tags.push(tag);
    });
    return Array.from(categoryMap.values());
  }, [tags, incentiveTagCategoryVersion]);

  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      setLocalPositiveTagIds(pointsAssignment.positiveTagIds || []);
      setLocalNegativeTagIds(pointsAssignment.negativeTagIds || []);
    } else {
      setLocalPositiveTagIds(pointsAssignment.positiveTagIds || []);
      setLocalNegativeTagIds(pointsAssignment.negativeTagIds || []);
    }
    onOpenChange(isOpen);
  };

  const toggleTag = (tagId: number) => {
    const filteredPositive = localPositiveTagIds.filter((id) => id !== tagId);
    const filteredNegative = localNegativeTagIds.filter((id) => id !== tagId);
    const isPositive = localPositiveTagIds.includes(tagId);
    const isNegative = localNegativeTagIds.includes(tagId);

    if (!isPositive && !isNegative) {
      filteredPositive.push(tagId);
    } else if (isPositive) {
      filteredNegative.push(tagId);
    }

    setLocalPositiveTagIds(filteredPositive);
    setLocalNegativeTagIds(filteredNegative);
  };

  const getTagState = (tagId: number): "positive" | "negative" | "neutral" => {
    if (localPositiveTagIds.includes(tagId)) return "positive";
    if (localNegativeTagIds.includes(tagId)) return "negative";
    return "neutral";
  };

  const getTotalPoints = (): number => {
    let total = 0;
    localPositiveTagIds.forEach((tagId) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tag = tags.find((t: any) => t.id === tagId);
      if (tag) total += tag.categoryPoints;
    });
    localNegativeTagIds.forEach((tagId) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tag = tags.find((t: any) => t.id === tagId);
      if (tag) total -= tag.categoryPoints;
    });
    return total;
  };

  const runAIPointAdjustment = async () => {
    if (!documentId) {
      toast.error("Please upload an image first to use AI Adjustment");
      return;
    }
    if (tags.length === 0) {
      toast.error("No tags available");
      return;
    }

    setIsAnalyzing(true);
    try {
      const selectedBoard = getSavedBoardSelection();
      const result = await assignAITags({
        board_id: selectedBoard.id,
        document_id: documentId,
      });
      setLocalPositiveTagIds(result.data.positive_tag_ids);
      setLocalNegativeTagIds(result.data.negative_tag_ids);
      toast.success("AI analysis complete");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      reportError({
        error,
        provider: "openai",
        fallback: "Unable to analyze image. Please try again.",
        title: "AI Point Adjustment failed",
      });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSave = () => {
    onPointsChange({
      positiveTagIds: localPositiveTagIds,
      negativeTagIds: localNegativeTagIds,
    });
    onOpenChange(false);
  };

  const canOpenSettings = Boolean(user?.can?.(GRANTS.grant_one));
  const total = getTotalPoints();

  return (
    <SpotIncentivePickerShell
      open={open}
      onClose={() => handleOpenChange(false)}
      presentation={presentation}
      title="Adjust Points"
      description="Tap a tag: positive → negative → clear"
      size={{
        width: "auto",
        height: "auto",
        minWidth: "min(90vw, 45rem)",
        minHeight: "min(90vh, 40rem)",
        maxWidth: "90vw",
        maxHeight: "90vh",
      }}
      wrapBody={false}
      dialogClassName="sm:max-w-[45rem]"
      headerEnd={
        presentation === "inline" || !canOpenSettings ? null : (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-9 shrink-0 rounded-full border-input bg-muted"
            aria-label="Incentive settings"
            onClick={() => {
              viewManager.openAnonymousViewByType({
                viewType: VIEW_TYPES.settings,
                viewName: "Settings",
                otherParams: { setting: SETTING_KEYS.INCENTIVE_CATEGORIES_AND_TAGS },
              });
            }}
          >
            <Settings className="size-4" />
          </Button>
        )
      }
      footer={
        <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span
              className={cn(
                "inline-flex h-9 items-center rounded-full border px-3 font-normal tabular-nums",
                total < 0
                  ? "border-destructive/30 bg-destructive/10 text-destructive"
                  : total > 0
                    ? "border-success/30 bg-success/10 text-success"
                    : "border-border bg-muted text-foreground",
              )}
            >
              {total > 0 ? "+" : ""}
              {total} pts
            </span>
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <ThumbsUp className="size-3.5 text-success" />
              {localPositiveTagIds.length}
            </span>
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <ThumbsDown className="size-3.5 text-destructive" />
              {localNegativeTagIds.length}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={runAIPointAdjustment}
              disabled={isAnalyzing || !documentId || tags.length === 0}
              className="h-9 gap-1.5 rounded-full px-3 text-sm font-normal"
            >
              <Sparkles className="size-3.5" />
              {isAnalyzing ? "Analyzing…" : "AI Adjustment"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              className="h-9 rounded-full px-3 text-sm font-normal"
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} className="h-9 rounded-full px-3 text-sm font-normal">
              Done
            </Button>
          </div>
        </div>
      }
    >
      <div
        className={cn(
          "min-h-0 flex-1 space-y-5 overflow-y-auto",
          presentation === "inline" ? "px-0.5 pt-0 pb-1" : "px-5 py-4 sm:px-6",
        )}
      >
        {categories.length > 0 ? (
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          categories.map((category: any) => (
            <div key={category.id} className="space-y-2.5">
              <div className="flex items-center gap-2">
                <div
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: category.color }}
                />
                <h3 className="text-sm font-normal text-foreground">
                  {category.name}{" "}
                  <span className="text-muted-foreground">({category.points} pts each)</span>
                </h3>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {category.tags.map((tag: any) => {
                  const state = getTagState(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleTag(tag.id)}
                      className={cn(
                        "inline-flex h-9 items-center gap-1.5 rounded-full border px-3 text-sm font-normal transition-colors",
                        state === "positive" &&
                          "border-success/30 bg-success/10 text-success hover:bg-success/15",
                        state === "negative" &&
                          "border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/15",
                        state === "neutral" &&
                          "border-border bg-muted/50 text-foreground hover:bg-muted",
                      )}
                    >
                      {state === "positive" ? <ThumbsUp className="size-3" /> : null}
                      {state === "negative" ? <ThumbsDown className="size-3" /> : null}
                      <span>{tag.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div className="rounded-xl border border-dashed border-border/60 p-6 text-center">
            <p className="text-sm text-muted-foreground">
              No tags available. Create tags in Board Settings → Categories & Tags.
            </p>
          </div>
        )}
      </div>
    </SpotIncentivePickerShell>
  );
};

export default SpotIncentivePointsAssignment;
